import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { UseFormReturn } from "react-hook-form";

import { loadDraft } from "@RequestFormV2/api/draftApi";
import { useRequestFormMode } from "@RequestFormV2/context/requestFormMode";
import { resolveFirstActiveSection, trackFormReentry } from "@RequestFormV2/formEvents/requestFormAnalytics";
import errorToast from "helpers/errorToast";
import { modifyDraftData } from "helpers/requestFormHelper";
import usePermissions from "hooks/usePermissions";
import { getRequestFormV2DefaultValues } from "@RequestFormV2/utils/getDefaultFormValues";
import { normalizeLoadedDraftData } from "@RequestFormV2/utils/normalizeDraftSchedule";

type UseRequestFormDraftLoadParams = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  methods: UseFormReturn<any>;
  isEditQuickRequest?: boolean;
};

export function useRequestFormDraftLoad({ methods, isEditQuickRequest = false }: UseRequestFormDraftLoadParams) {
  const mode = useRequestFormMode();
  const isQuoteRequest = mode === "quote";
  const [searchParams, setSearchParams] = useSearchParams();
  const draftId = searchParams.get("draft_id");
  const isDuplicated = searchParams.get("is_duplicated");
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const { permissions } = usePermissions();

  const { enable_new_address_structure } = JSON.parse(permissions || "{}")?.company_shipper_settings || {};

  useEffect(() => {
    // LocationDetailsSection does not preselect the sole payment location while draft_id or
    // shipmentId is in the URL, so a real draft (convert/duplicate/edit) never has a location
    // here yet. A location present at this point means a draft was already applied.
    if (!draftId || methods.getValues("organizational.location")) {
      return;
    }

    setIsLoading(true);
    setLoadFailed(false);
    loadDraft(draftId, mode)
      .then((res) => {
        if (!Object.keys(res.data).length) return;

        const modified = modifyDraftData(
          res.data,
          Boolean(isDuplicated),
          isQuoteRequest,
          enable_new_address_structure,
        ) as Record<string, unknown>;

        const normalized = normalizeLoadedDraftData(modified);
        const nextValues = {
          ...getRequestFormV2DefaultValues(),
          ...normalized,
          draftSaved: false,
        };
        methods.reset(nextValues);
        trackFormReentry({
          draftId,
          firstFieldActive: resolveFirstActiveSection(nextValues as Record<string, unknown>),
        });
      })
      .catch((err) => {
        setLoadFailed(true);
        if (err?.response?.status === 404 && !isEditQuickRequest) {
          searchParams.delete("draft_id");
          setSearchParams(searchParams);
        }
        errorToast(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    draftId,
    isQuoteRequest,
    isDuplicated,
    enable_new_address_structure,
    isEditQuickRequest,
    methods,
    mode,
    searchParams,
    setSearchParams,
  ]);

  return { isLoading, loadFailed, draftId };
}
